import {
    closestCenter,
    DndContext,
    type DragEndEvent,
    type DragOverEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import type { TaskStatusOutput } from "@orsane/schema/tasks";
import { useQuery } from "@tanstack/react-query";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { updateTaskStatusOrderMutation, useDeleteTaskStatusMutation } from "@/mutations/mutations";
import { taskStatusesQueryOptions } from "@/queries/queries";
import { AlertDialogType } from "@/types/alert-dialog";
import { type AlertDialogImperativeHandle, DefaultAlertDialog } from "../DefaultAlertDialog";
import { NonSortableTaskStatusRow, SortableTaskStatusRow } from "./SortableTaskStatusRow";
import { TaskStatusDialog } from "./TaskStatusDialog";
import { createTaskStatusColumns } from "./task-status-columns";

export default function TaskStatusSettings() {
    const deleteAlertDialogRef = useRef<AlertDialogImperativeHandle | null>(null);
    const { mutateAsync: updateTaskStatusOrder } = updateTaskStatusOrderMutation();
    const { data: statusesData = [], isLoading: isLoadingStatuses } = useQuery(taskStatusesQueryOptions());
    const { mutateAsync: deleteTaskStatus } = useDeleteTaskStatusMutation();

    const [statuses, setStatuses] = useState(statusesData);
    const [editingStatus, setEditingStatus] = useState<TaskStatusOutput | "new" | null>(null);

    // Sync statuses with query data
    useEffect(() => {
        if (statusesData !== statuses) {
            setStatuses(statusesData);
        }
    }, [statusesData]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            if (!event.active || !event.over) return;
            if (event.active.id === event.over.id) return;

            const oldIndex = statuses.findIndex((item) => item.id.toString() === event.active.id.toString());
            const newIndex = statuses.findIndex((item) => item.id.toString() === event.over!.id.toString());

            if (newIndex === 0) {
                // you cannot swap with the first item
                return;
            }

            if (newIndex === statuses.length - 1) {
                // you cannot swap with the last item
                return;
            }

            if (oldIndex !== -1 && newIndex !== -1) {
                const reorderedStatuses = arrayMove(statuses, oldIndex, newIndex);
                const newOrderIds = reorderedStatuses.map((item) => item.id);

                console.log(reorderedStatuses, oldIndex, newIndex, newOrderIds);

                setStatuses(reorderedStatuses);
                updateTaskStatusOrder({ orderedIds: newOrderIds });
            }
        },
        [statuses]
    );

    const handleDragOver = useCallback(
        (event: DragOverEvent) => {
            if (!event.over) return;
            if (event.active.id === event.over.id) return;

            const oldIndex = statuses.findIndex((item) => item.id.toString() === event.active.id.toString());
            const newIndex = statuses.findIndex((item) => item.id.toString() === event.over!.id.toString());

            console.log(oldIndex, newIndex);
            return false;
        },
        [statuses]
    );

    const sortableItems = useMemo(() => statuses.map((status) => status.id.toString()), [statuses]);

    const handleEdit = (status: TaskStatusOutput) => {
        setEditingStatus(status);
    };

    const handleDelete = async (id: number) => {
        const confirmed = await deleteAlertDialogRef.current!.show();
        if (confirmed) {
            await deleteTaskStatus({ id: id });
        }
    };

    const handleAfterSubmit = async () => {
        setEditingStatus(null);
    };

    const columns = createTaskStatusColumns(handleEdit, handleDelete);

    const table = useReactTable({
        data: statuses,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => String(row.id),
        manualSorting: true,
    });

    if (isLoadingStatuses) {
        return null;
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-end">
                <Button
                    variant="outline"
                    onClick={() => {
                        setEditingStatus("new");
                    }}
                >
                    <Plus />
                    Add Status
                </Button>
            </div>

            <div className="rounded-md border bg-white">
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                >
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    <TableHead className="w-10"></TableHead>
                                    {headerGroup.headers.map((header) => {
                                        return (
                                            <TableHead key={header.id}>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </TableHead>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
                                <NonSortableTaskStatusRow row={table.getRowModel().rows[0]}></NonSortableTaskStatusRow>
                                {table
                                    .getRowModel()
                                    .rows.filter((row) => !row.original.isSystem)
                                    .map((row) => (
                                        <SortableTaskStatusRow
                                            isFirst={row.index === 0}
                                            isLast={row.index === table.getRowModel().rows.length - 1}
                                            key={row.original.id}
                                            status={row.original}
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell
                                                    className={[
                                                        cell.column.columnDef.meta?.width
                                                            ? `w-[${cell.column.columnDef.meta?.width}px]`
                                                            : "",
                                                        cell.column.columnDef.meta?.align
                                                            ? `text-${cell.column.columnDef.meta?.align}`
                                                            : "",
                                                    ]
                                                        .filter(Boolean)
                                                        .join(" ")}
                                                    key={cell.id}
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TableCell>
                                            ))}
                                        </SortableTaskStatusRow>
                                    ))}
                                <NonSortableTaskStatusRow
                                    row={table.getRowModel().rows[table.getRowModel().rows.length - 1]}
                                ></NonSortableTaskStatusRow>
                            </SortableContext>
                        </TableBody>
                    </Table>
                </DndContext>
            </div>

            <TaskStatusDialog
                status={editingStatus}
                open={!!editingStatus}
                onOpenChange={(o) => !o && setEditingStatus(null)}
                onSubmit={handleAfterSubmit}
            />

            <DefaultAlertDialog
                ref={deleteAlertDialogRef}
                type={AlertDialogType.Delete}
                title="Delete this status?"
                description={`This action cannot be undone. Tasks using this status will be re-assigned to the default status of 'todo'.`}
            />
        </div>
    );
}
